/**
 * Core IndexedDB infrastructure: initialization, migration, and generic CRUD helpers.
 * All domain-specific store modules import from this file.
 */

import { generateId } from '../utils';

const DB_NAME = 'easeverse_reader';
const DB_VERSION = 14; // v14: defensive re-check — some users' v13 upgrade committed
                       //      version=13 but didn't actually create the note_revisions
                       //      store (same intermittent IDB partial-commit bug as
                       //      v6→v9 chunk-stores history).  v14 drops the oldVersion
                       //      gate so every open re-asserts the schema.
                       // v13: add note_revisions store + parent_note_id index on notes
                       //      (Deep Reading: replies + edit history, 2026-05-20)
                       // v12: tolerate _perf_trace store left over from a buggy diagnostic build (092b326)
                       //      that briefly bumped this DB to v12 to add it. The store is harmless;
                       //      we don't use it anymore (perfLog moved to a separate database).
                       // v11: add book_covers store

let db = null;
let dbFailed = false; // If true, skip all DB operations (DB is unavailable)

/** Get the current DB instance (for sub-modules that need direct access). */
export function getDB() {
    return db;
}

// Check if DB is available (for callers that need to guard operations)
export function isDBAvailable() {
    return db !== null && !dbFailed;
}

/**
 * Reset module-level DB state. Used after iOS Safari bfcache restore (the
 * frozen JS heap brings `dbFailed=true` and a possibly-stale `db` reference
 * back into the live page) and any other case where we want to allow a
 * fresh `initDB()` retry.
 *
 * Closes the current connection (if any) and clears both `db` and
 * `dbFailed` so the next `ensureDB()` call reopens IndexedDB cleanly.
 */
export function resetDbState() {
    if (dbFailed) {
        console.info('[LocalDB] Resetting dbFailed (likely bfcache restore)');
    }
    dbFailed = false;
    if (db) {
        try { db.close(); } catch { /* may already be closed by versionchange */ }
        db = null;
    }
}

// Initialize IndexedDB
export async function initDB() {
    if (db) return db;
    if (dbFailed) return null; // Don't retry after failure

    return new Promise((resolve, reject) => {
        // Safety timeout: if DB open hangs (e.g. blocked or corrupted), don't wait forever
        const timeout = setTimeout(() => {
            console.warn('[LocalDB] initDB timed out after 5s (DB may be blocked or corrupted)');
            console.warn('[LocalDB] Try: DevTools → Application → IndexedDB → Delete "easeverse_reader"');
            dbFailed = true;
            reject(new Error('IndexedDB open timed out'));
        }, 5000);

        let request;
        try {
            request = indexedDB.open(DB_NAME, DB_VERSION);
        } catch (e) {
            clearTimeout(timeout);
            console.error('[LocalDB] indexedDB.open() threw:', e);
            dbFailed = true;
            reject(e);
            return;
        }

        request.onerror = () => {
            clearTimeout(timeout);
            console.error('[LocalDB] DB open error:', request.error);
            dbFailed = true;
            reject(request.error);
        };

        request.onsuccess = () => {
            clearTimeout(timeout);
            db = request.result;
            dbFailed = false;
            // Listen for version change requests from other tabs
            db.onversionchange = () => {
                db.close();
                db = null;
            };
            // Safety check: if chunk stores are missing after successful open,
            // the upgrade transaction somehow committed without creating them.
            if (!db.objectStoreNames.contains('chapter_chunks')) {
                console.error('[LocalDB] DB at v%d but chunk stores missing (stores=%s). Chunk features will use cloud-only fallback.',
                    db.version, [...db.objectStoreNames].join(','));
            }
            // Same pattern for note_revisions — caught a real silent v13
            // partial-commit on 2026-05-20 (version=13 but store missing).
            // v14 unconditional re-check should have fixed it; this warning
            // exists so the next regression doesn't hide for another sprint.
            if (!db.objectStoreNames.contains('note_revisions')) {
                console.error('[LocalDB] DB at v%d but note_revisions store missing (stores=%s). Deep Reading edit history will show empty.',
                    db.version, [...db.objectStoreNames].join(','));
            }
            resolve(db);
        };

        // Handle blocked event: another tab has the DB open at an older version
        request.onblocked = () => {
            console.warn('[LocalDB] DB upgrade blocked by another tab');
            clearTimeout(timeout);
            dbFailed = true;
            reject(new Error('IndexedDB upgrade blocked by another tab'));
        };

        request.onupgradeneeded = (event) => {
            const database = event.target.result;

            // Books store
            if (!database.objectStoreNames.contains('books')) {
                const booksStore = database.createObjectStore('books', { keyPath: 'id' });
                booksStore.createIndex('user_id', 'user_id', { unique: false });
                booksStore.createIndex('updated_at', 'updated_at', { unique: false });
            }

            // Highlights store
            if (!database.objectStoreNames.contains('highlights')) {
                const highlightsStore = database.createObjectStore('highlights', { keyPath: 'id' });
                highlightsStore.createIndex('book_id', 'book_id', { unique: false });
                highlightsStore.createIndex('user_id', 'user_id', { unique: false });
                highlightsStore.createIndex('updated_at', 'updated_at', { unique: false });
            }

            // Notes store
            if (!database.objectStoreNames.contains('notes')) {
                const notesStore = database.createObjectStore('notes', { keyPath: 'id' });
                notesStore.createIndex('book_id', 'book_id', { unique: false });
                notesStore.createIndex('user_id', 'user_id', { unique: false });
                notesStore.createIndex('updated_at', 'updated_at', { unique: false });
            }

            // Bookmarks store
            if (!database.objectStoreNames.contains('bookmarks')) {
                const bookmarksStore = database.createObjectStore('bookmarks', { keyPath: 'id' });
                bookmarksStore.createIndex('book_id', 'book_id', { unique: false });
                bookmarksStore.createIndex('user_id', 'user_id', { unique: false });
                bookmarksStore.createIndex('updated_at', 'updated_at', { unique: false });
            }

            // Progress store
            if (!database.objectStoreNames.contains('progress')) {
                const progressStore = database.createObjectStore('progress', { keyPath: 'id' });
                progressStore.createIndex('book_id', 'book_id', { unique: false });
                progressStore.createIndex('user_id', 'user_id', { unique: false });
                progressStore.createIndex('user_book', ['user_id', 'book_id'], { unique: true });
            }

            // Sync metadata store
            if (!database.objectStoreNames.contains('sync_meta')) {
                database.createObjectStore('sync_meta', { keyPath: 'key' });
            }

            // Pending changes store (for offline mutations)
            if (!database.objectStoreNames.contains('pending_changes')) {
                const pendingStore = database.createObjectStore('pending_changes', { keyPath: 'id', autoIncrement: true });
                pendingStore.createIndex('store', 'store', { unique: false });
                pendingStore.createIndex('created_at', 'created_at', { unique: false });
            }

            // Book files store (for local file storage)
            if (!database.objectStoreNames.contains('book_files')) {
                database.createObjectStore('book_files', { keyPath: 'id' });
            }

            // v5: add highlight_id index to notes store
            if (event.oldVersion < 5 && database.objectStoreNames.contains('notes')) {
                const notesStore = event.target.transaction.objectStore('notes');
                if (!notesStore.indexNames.contains('highlight_id')) {
                    notesStore.createIndex('highlight_id', 'highlight_id', { unique: false });
                }
            }

            // v6: add chunk system stores
            if (event.oldVersion < 6) {
                if (!database.objectStoreNames.contains('canonical_books')) {
                    const store = database.createObjectStore('canonical_books', { keyPath: 'canonical_book_id' });
                    store.createIndex('isbn', 'isbn', { unique: false });
                    store.createIndex('simhash', 'simhash', { unique: false });
                }

                if (!database.objectStoreNames.contains('chapter_chunks')) {
                    const store = database.createObjectStore('chapter_chunks', { keyPath: 'chapter_content_hash' });
                    store.createIndex('canonical_book_id', 'canonical_book_id', { unique: false });
                    store.createIndex('prompt_version', 'prompt_version', { unique: false });
                }

                if (!database.objectStoreNames.contains('chapter_registry')) {
                    const store = database.createObjectStore('chapter_registry', { keyPath: 'id' });
                    store.createIndex('canonical_book_id', 'canonical_book_id', { unique: false });
                }

                if (!database.objectStoreNames.contains('chunk_explanations')) {
                    const store = database.createObjectStore('chunk_explanations', { keyPath: 'id' });
                    store.createIndex('lookup', ['chunk_text', 'source_language', 'target_language'], { unique: false });
                    store.createIndex('chapter', 'chapter_content_hash', { unique: false });
                }

                if (!database.objectStoreNames.contains('user_chunk_profile')) {
                    const store = database.createObjectStore('user_chunk_profile', { keyPath: 'id' });
                    store.createIndex('user_id', 'user_id', { unique: false });
                }

                if (!database.objectStoreNames.contains('chunk_interactions')) {
                    const store = database.createObjectStore('chunk_interactions', { keyPath: 'id', autoIncrement: true });
                    store.createIndex('user_book', ['user_id', 'book_id'], { unique: false });
                    store.createIndex('chunk_hash_text', ['chapter_content_hash', 'chunk_text'], { unique: false });
                }
            }

            // v7: ensure chunk stores exist (in case DB was already at v6 before chunk code was deployed)
            if (event.oldVersion < 7) {
                if (!database.objectStoreNames.contains('canonical_books')) {
                    const store = database.createObjectStore('canonical_books', { keyPath: 'canonical_book_id' });
                    store.createIndex('isbn', 'isbn', { unique: false });
                    store.createIndex('simhash', 'simhash', { unique: false });
                }
                if (!database.objectStoreNames.contains('chapter_chunks')) {
                    const store = database.createObjectStore('chapter_chunks', { keyPath: 'chapter_content_hash' });
                    store.createIndex('canonical_book_id', 'canonical_book_id', { unique: false });
                    store.createIndex('prompt_version', 'prompt_version', { unique: false });
                }
                if (!database.objectStoreNames.contains('chapter_registry')) {
                    const store = database.createObjectStore('chapter_registry', { keyPath: 'id' });
                    store.createIndex('canonical_book_id', 'canonical_book_id', { unique: false });
                }
                if (!database.objectStoreNames.contains('chunk_explanations')) {
                    const store = database.createObjectStore('chunk_explanations', { keyPath: 'id' });
                    store.createIndex('lookup', ['chunk_text', 'source_language', 'target_language'], { unique: false });
                    store.createIndex('chapter', 'chapter_content_hash', { unique: false });
                }
                if (!database.objectStoreNames.contains('user_chunk_profile')) {
                    const store = database.createObjectStore('user_chunk_profile', { keyPath: 'id' });
                    store.createIndex('user_id', 'user_id', { unique: false });
                }
                if (!database.objectStoreNames.contains('chunk_interactions')) {
                    const store = database.createObjectStore('chunk_interactions', { keyPath: 'id', autoIncrement: true });
                    store.createIndex('user_book', ['user_id', 'book_id'], { unique: false });
                    store.createIndex('chunk_hash_text', ['chapter_content_hash', 'chunk_text'], { unique: false });
                }
            }

            // v8: ensure chunk stores exist (fixes users whose DB reached v7 before chunk code deployed)
            if (event.oldVersion < 8) {
                if (!database.objectStoreNames.contains('canonical_books')) {
                    const store = database.createObjectStore('canonical_books', { keyPath: 'canonical_book_id' });
                    store.createIndex('isbn', 'isbn', { unique: false });
                    store.createIndex('simhash', 'simhash', { unique: false });
                }
                if (!database.objectStoreNames.contains('chapter_chunks')) {
                    const store = database.createObjectStore('chapter_chunks', { keyPath: 'chapter_content_hash' });
                    store.createIndex('canonical_book_id', 'canonical_book_id', { unique: false });
                    store.createIndex('prompt_version', 'prompt_version', { unique: false });
                }
                if (!database.objectStoreNames.contains('chapter_registry')) {
                    const store = database.createObjectStore('chapter_registry', { keyPath: 'id' });
                    store.createIndex('canonical_book_id', 'canonical_book_id', { unique: false });
                }
                if (!database.objectStoreNames.contains('chunk_explanations')) {
                    const store = database.createObjectStore('chunk_explanations', { keyPath: 'id' });
                    store.createIndex('lookup', ['chunk_text', 'source_language', 'target_language'], { unique: false });
                    store.createIndex('chapter', 'chapter_content_hash', { unique: false });
                }
                if (!database.objectStoreNames.contains('user_chunk_profile')) {
                    const store = database.createObjectStore('user_chunk_profile', { keyPath: 'id' });
                    store.createIndex('user_id', 'user_id', { unique: false });
                }
                if (!database.objectStoreNames.contains('chunk_interactions')) {
                    const store = database.createObjectStore('chunk_interactions', { keyPath: 'id', autoIncrement: true });
                    store.createIndex('user_book', ['user_id', 'book_id'], { unique: false });
                    store.createIndex('chunk_hash_text', ['chapter_content_hash', 'chunk_text'], { unique: false });
                }
            }

            // v9: unconditional — create any missing chunk stores regardless of oldVersion.
            // Previous versioned blocks (v6/v7/v8) all failed for some users, likely due to
            // upgrade transactions committing before chunk store creation could execute.
            // Using !contains checks means this is safe to run even if stores already exist.
            if (!database.objectStoreNames.contains('canonical_books')) {
                const store = database.createObjectStore('canonical_books', { keyPath: 'canonical_book_id' });
                store.createIndex('isbn', 'isbn', { unique: false });
                store.createIndex('simhash', 'simhash', { unique: false });
            }
            if (!database.objectStoreNames.contains('chapter_chunks')) {
                const store = database.createObjectStore('chapter_chunks', { keyPath: 'chapter_content_hash' });
                store.createIndex('canonical_book_id', 'canonical_book_id', { unique: false });
                store.createIndex('prompt_version', 'prompt_version', { unique: false });
            }
            if (!database.objectStoreNames.contains('chapter_registry')) {
                const store = database.createObjectStore('chapter_registry', { keyPath: 'id' });
                store.createIndex('canonical_book_id', 'canonical_book_id', { unique: false });
            }
            if (!database.objectStoreNames.contains('chunk_explanations')) {
                const store = database.createObjectStore('chunk_explanations', { keyPath: 'id' });
                store.createIndex('lookup', ['chunk_text', 'source_language', 'target_language'], { unique: false });
                store.createIndex('chapter', 'chapter_content_hash', { unique: false });
            }
            if (!database.objectStoreNames.contains('user_chunk_profile')) {
                const store = database.createObjectStore('user_chunk_profile', { keyPath: 'id' });
                store.createIndex('user_id', 'user_id', { unique: false });
            }
            if (!database.objectStoreNames.contains('chunk_interactions')) {
                const store = database.createObjectStore('chunk_interactions', { keyPath: 'id', autoIncrement: true });
                store.createIndex('user_book', ['user_id', 'book_id'], { unique: false });
                store.createIndex('chunk_hash_text', ['chapter_content_hash', 'chunk_text'], { unique: false });
            }

            // v10: remove shelves store (feature removed)
            if (database.objectStoreNames.contains('shelves')) {
                database.deleteObjectStore('shelves');
            }

            // v11: book covers cache (persists extracted covers to avoid re-extraction)
            if (!database.objectStoreNames.contains('book_covers')) {
                database.createObjectStore('book_covers', { keyPath: 'id' });
            }

            // v13/v14: note_revisions store + parent_note_id index on notes.
            //
            // Append-only snapshots of OLD content captured by the backend
            // trigger on each content edit.  Client is read-only (sync pulls
            // them; no local writes outside bulkPut).
            //
            // **UNCONDITIONAL** ensure — NOT gated on oldVersion.  Mirrors the
            // v9 chunk-stores recovery pattern: some users' v13 upgrade
            // silently committed version=13 without creating the store
            // (intermittent IDB upgrade partial-commit), so every open must
            // re-check.  Safe because `!contains()` guards each create.
            //
            // Indices:
            //   - user_id   — clear-on-logout cursor and pruning
            //   - note_id   — fetch a single note's history for the timeline
            //                 modal (newest → oldest)
            //
            // Also: add parent_note_id index on notes for threaded-reply
            // rendering ("get all direct children of note X").
            //
            // Wrapped in try/log because the 2026-05-20 failure mode was
            // v14 transaction committing without creating note_revisions and
            // NO console error — the abort was silent.  Logging every branch
            // means the next regression names its own line.
            try {
                console.log('[LocalDB upgrade] v13/v14 enter; oldVersion=%d, contains note_revisions=%s',
                    event.oldVersion, database.objectStoreNames.contains('note_revisions'));
                if (!database.objectStoreNames.contains('note_revisions')) {
                    console.log('[LocalDB upgrade] creating note_revisions store…');
                    const revStore = database.createObjectStore('note_revisions', { keyPath: 'id' });
                    revStore.createIndex('user_id', 'user_id', { unique: false });
                    revStore.createIndex('note_id', 'note_id', { unique: false });
                    console.log('[LocalDB upgrade] note_revisions store + indices created');
                }
                if (database.objectStoreNames.contains('notes')) {
                    const notesStore = event.target.transaction.objectStore('notes');
                    if (!notesStore.indexNames.contains('parent_note_id')) {
                        console.log('[LocalDB upgrade] creating notes.parent_note_id index…');
                        notesStore.createIndex('parent_note_id', 'parent_note_id', { unique: false });
                    }
                }
                // last_sync reset: drop the cursor ONCE during the v13/v14
                // climb so the next sync runs as a FULL sync and back-fills
                // any revisions the backend trigger accumulated before this
                // client had a note_revisions store to receive them.
                // Gated so re-opens at v14 don't keep resetting forever.
                if (event.oldVersion < 14 && database.objectStoreNames.contains('sync_meta')) {
                    console.log('[LocalDB upgrade] clearing sync_meta.last_sync (forces next sync to full)');
                    const metaStore = event.target.transaction.objectStore('sync_meta');
                    metaStore.delete('last_sync');
                }
            } catch (e) {
                // versionchange transactions auto-abort on uncaught errors,
                // taking us BACK to the previous version with no schema
                // change.  Surface the error so we can see which line blew
                // up.  Re-throw to keep the abort semantics intact (we DO
                // want a partial schema to roll back, not be left half-built).
                console.error('[LocalDB upgrade] v13/v14 block threw — transaction will abort:', e);
                throw e;
            }
        };
    });
}

/**
 * Migrate highlight.note → separate note records (v4→v5 data migration).
 * Runs once after DB opens at v5. Idempotent: checks a sync_meta flag.
 */
export async function migrateHighlightNotes() {
    if (!db) return;
    // Check if migration already ran
    try {
        const meta = await new Promise((resolve, reject) => {
            const tx = db.transaction('sync_meta', 'readonly');
            const req = tx.objectStore('sync_meta').get('notes_migrated_v5');
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
        if (meta?.value) return; // Already migrated
    } catch { /* proceed */ }

    const tx = db.transaction(['highlights', 'notes', 'pending_changes', 'sync_meta'], 'readwrite');
    const highlightsStore = tx.objectStore('highlights');
    const notesStore = tx.objectStore('notes');
    const pendingStore = tx.objectStore('pending_changes');
    const metaStore = tx.objectStore('sync_meta');

    const allHighlights = await new Promise((resolve, reject) => {
        const req = highlightsStore.getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
    });

    const timestamp = new Date().toISOString();
    for (const h of allHighlights) {
        if (h.deleted_at) continue;
        if (!h.note || !h.note.trim()) continue;

        // Create a note record from highlight.note
        const noteId = generateId();
        const isNoteOnly = h.style === 'note-only';
        const note = {
            id: noteId,
            user_id: h.user_id,
            book_id: h.book_id,
            content: h.note,
            cfi_range: h.cfi_range,
            selected_text: h.text,
            highlight_id: isNoteOnly ? null : h.id,
            chapter: h.chapter || null,
            created_at: h.created_at || timestamp,
            updated_at: timestamp,
            deleted_at: null,
            _pending: true,
        };
        notesStore.put(note);
        pendingStore.put({ id: `notes:${noteId}`, store: 'notes', action: 'create', data: note, updated_at: timestamp });

        // Clear note from highlight and update
        const updated = { ...h, note: null, updated_at: timestamp, _pending: true };
        if (isNoteOnly) {
            // note-only highlights become soft-deleted
            updated.deleted_at = timestamp;
        }
        highlightsStore.put(updated);
        pendingStore.put({
            id: `highlights:${h.id}`,
            store: 'highlights',
            action: isNoteOnly ? 'delete' : 'update',
            data: updated,
            updated_at: timestamp,
        });
    }

    metaStore.put({ key: 'notes_migrated_v5', value: true });

    await new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
    });
}

// =====================================================
// Generic CRUD helpers (used by all store modules)
// =====================================================

// Helper: ensure DB is ready, return false if unavailable
export async function ensureDB() {
    if (db) return true;
    if (dbFailed) return false;
    try {
        await initDB();
        return !!db;
    } catch {
        return false;
    }
}

// Get current timestamp
export function now() {
    return new Date().toISOString();
}

export async function getAll(storeName, userId) {
    if (!(await ensureDB())) return [];
    if (!hasStore(storeName)) return [];
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const index = store.index('user_id');
        const request = index.getAll(userId);

        request.onsuccess = () => {
            // Filter out soft-deleted items
            const results = request.result.filter(item => !item.deleted_at);
            resolve(results);
        };
        request.onerror = () => reject(request.error);
    });
}

/** Get all items for user without filtering deleted_at (for full-replace sync). */
export async function getAllForUserRaw(storeName, userId) {
    if (!(await ensureDB())) return [];
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const index = store.index('user_id');
        const request = index.getAll(userId);
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
    });
}

export async function getByBookId(storeName, bookId, userId) {
    if (!(await ensureDB())) return [];
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const index = store.index('book_id');
        const request = index.getAll(bookId);

        request.onsuccess = () => {
            // Filter by user and exclude soft-deleted
            const results = request.result.filter(
                item => item.user_id === userId && !item.deleted_at
            );
            resolve(results);
        };
        request.onerror = () => reject(request.error);
    });
}

/**
 * Check if a store exists in the current DB instance.
 * Returns false if the DB hasn't upgraded to include new stores yet
 * (e.g., user has another tab open with old version).
 */
function hasStore(storeName) {
    const exists = db && db.objectStoreNames.contains(storeName);
    if (!exists && db) {
        console.warn('[LocalDB] Store "%s" not found in DB (version=%d, stores=%s)', storeName, db.version, [...db.objectStoreNames].join(','));
    }
    return exists;
}

export async function getById(storeName, id) {
    if (!(await ensureDB())) return null;
    if (!hasStore(storeName)) return null;
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const request = store.get(id);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

export async function put(storeName, data) {
    if (!(await ensureDB())) return data;
    if (!hasStore(storeName)) return data;
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const request = store.put(data);

        request.onsuccess = () => resolve(data);
        request.onerror = () => reject(request.error);
    });
}

export async function remove(storeName, id) {
    if (!(await ensureDB())) return;
    if (!hasStore(storeName)) return;
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const request = store.delete(id);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

/**
 * Record a pending local change. Uses a deterministic key (store:entity_id)
 * so repeated mutations to the same entity overwrite the previous record
 * instead of accumulating duplicate entries.
 */
export async function addPendingChange(store, action, data) {
    if (!(await ensureDB())) return;
    return new Promise((resolve, reject) => {
        const tx = db.transaction('pending_changes', 'readwrite');
        const pendingStore = tx.objectStore('pending_changes');
        const request = pendingStore.put({
            id: `${store}:${data.id}`,
            store,
            action,
            data,
            updated_at: now(),
        });

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}
